/* eslint-disable no-extra-boolean-cast */
import React, { useEffect, useState } from 'react';
import { Checkbox, FormControlLabel, Grid2, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Controller, useFormContext } from 'react-hook-form';
import ExpenseTextField from './ExpenseTextField';
import { AmountCurrencyInputAdornment } from './AmountCurrencyInputAdornment';
import { valueAsExpense } from '../../Utils/ValueAsUtils';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { COLORS } from '../../../utils/Application_Constants';
import { ExpenseModeEnum } from './ExpenseMode.enum';

const styles = () => ({
    errorMessage: {
        color: COLORS.LT_SUNFLOWER,
        float: 'left',
        textAlign: 'left',
        fontSize: '13px',
        fontWeight: '500',
    },
});

const ExpenseAmount = (props) => {
    const {
        classes,
        disabled,
        expense,
        index,
        isAnyExpensePosted,
        isPendingSubmission,
        isPosted,
        isReimbursement,
        isViewOnly,
        isInvoiceReviewLocked = false,
        mode,
        originalExpense,
        originalExpenseEntries,
        updateAllAmounts = () => {},
        isCreditCardTransaction = false,
        enableEditByCorrection = false,
        size,
    } = props;
    const { control, errors, setValue, watch, clearErrors } = useFormContext();
    const watchExpenseEntries = watch('expenseEntries');
    const [required, setRequired] = useState({
        required:
            expense?.status !== ExpenseStatusValuesEnum.NonReimbursable &&
            expense?.status !== ExpenseStatusValuesEnum.Abandoned,
    });
    const [creditCardAmountChangeWarning, setCreditCardAmountChangeWarning] = useState('');

    const watchAmount = watch(`expenseEntries[${index}].amount`);
    const watchCurrency = watch(`expenseEntries[${index}].currency`);
    const currentStatus = watch(`expenseEntries[${index}].status`);

    useEffect(() => {
        if (mode === ExpenseModeEnum.CreditCard) {
            return setRequired({
                required:
                    currentStatus === ExpenseStatusValuesEnum.Approved ||
                    currentStatus === ExpenseStatusValuesEnum.InvoiceReview,
            });
        }

        return setRequired({
            required:
                expense?.status !== ExpenseStatusValuesEnum.NonReimbursable &&
                expense?.status !== ExpenseStatusValuesEnum.Abandoned,
        });
    }, [expense?.status, currentStatus, mode]);

    useEffect(() => {
        if (!required.required) {
            clearErrors(`expenseEntries[${index}].amount`);
        }
    }, [required.required, index, clearErrors]);

    const disableByCCAsCorrectionOrStatus =
        (isCreditCardTransaction && expense?.status === ExpenseStatusValuesEnum.Approved) ||
        (isCreditCardTransaction && expense?.status === ExpenseStatusValuesEnum.Posted) ||
        (isCreditCardTransaction && expense?.previousVersionID);

    useEffect(() => {
        if (isReimbursement) {
            for (let indexLoop = 0; indexLoop < watchExpenseEntries.length; ++indexLoop) {
                if (indexLoop !== index) {
                    // If it's not my amount
                    let originalForPage = null;
                    if (
                        originalExpenseEntries &&
                        originalExpenseEntries.length > 0 &&
                        watchExpenseEntries[indexLoop].id &&
                        watchExpenseEntries[indexLoop].id > 0
                    ) {
                        originalForPage = originalExpenseEntries.find(
                            (entry) => entry.id === watchExpenseEntries[indexLoop].id
                        );
                    }
                    if (
                        !originalExpenseEntries ||
                        !originalForPage ||
                        (originalForPage && originalForPage.status !== 'Posted')
                    ) {
                        // If the other tab original db expense is not posted update
                        if (watchExpenseEntries[indexLoop].amount !== watchAmount) {
                            setValue(`expenseEntries[${indexLoop}].amount`, watchAmount);
                        }
                    }
                }
            }
        }
    }, [watchAmount, setValue]);

    useEffect(() => {
        if (isReimbursement) {
            for (let indexLoop = 0; indexLoop < watchExpenseEntries.length; ++indexLoop) {
                if (indexLoop !== index) {
                    // If it's not my currency
                    let originalForPage = null;
                    if (
                        originalExpenseEntries &&
                        originalExpenseEntries.length > 0 &&
                        watchExpenseEntries[indexLoop].id &&
                        watchExpenseEntries[indexLoop].id > 0
                    ) {
                        originalForPage = originalExpenseEntries.find(
                            (entry) => entry.id === watchExpenseEntries[indexLoop].id
                        );
                    }
                    if (
                        !originalExpenseEntries ||
                        !originalForPage ||
                        (originalForPage && originalForPage.status !== 'Posted')
                    ) {
                        // If the other tab original db expense is not posted update
                        if (watchExpenseEntries[indexLoop].currency !== watchCurrency) {
                            setValue(`expenseEntries[${indexLoop}].currency`, watchCurrency);
                        }
                    }
                }
            }
        }
    }, [watchCurrency, setValue]);

    return (
        <Grid2 item size={size || { xs: 12, md: 6 }}>
            <Controller
                control={control}
                name={`expenseEntries[${index}].amount`}
                defaultValue={expense.amount}
                rules={{
                    required: required.required ? 'Expense amount is required' : false,
                }}
                render={({ onChange, value }) => (
                    <ExpenseTextField
                        {...required}
                        placeholder="0.00"
                        id="expense-amount"
                        onChange={(e) => {
                            onChange(valueAsExpense(e.target.value));
                            updateAllAmounts(e.target.value);
                            if (
                                isCreditCardTransaction &&
                                originalExpense &&
                                e.target.value !== originalExpense.amount.toString()
                            ) {
                                setCreditCardAmountChangeWarning(
                                    'Warning: Editing this amount will override the imported value and is logged for auditing.'
                                );
                            } else {
                                setCreditCardAmountChangeWarning(null);
                            }
                        }}
                        value={value}
                        label={
                            isCreditCardTransaction ? 'Charge Amount' : 'Submitted expense amount'
                        }
                        endAdornment={
                            <AmountCurrencyInputAdornment
                                disabled={
                                    isViewOnly ||
                                    isInvoiceReviewLocked ||
                                    disabled ||
                                    (isPosted && isViewOnly) ||
                                    isPendingSubmission ||
                                    enableEditByCorrection ||
                                    disableByCCAsCorrectionOrStatus ||
                                    (!!expense.id && !isReimbursement) ||
                                    (isReimbursement && expense.source === 'Clinician') ||
                                    (isReimbursement && index > 0) ||
                                    (isReimbursement &&
                                        index === 0 &&
                                        expense.source === 'LtAssociate' &&
                                        isAnyExpensePosted) ||
                                    (isReimbursement &&
                                        index === 0 &&
                                        expense.source === 'LtAssociate' &&
                                        originalExpense &&
                                        !(
                                            originalExpense.status === 'NeedMoreInformation' ||
                                            originalExpense.status === 'InProcess' ||
                                            originalExpense.status === 'ReadyForReview' ||
                                            originalExpense.status === 'ReturnedByAccounting' ||
                                            originalExpense.status === 'TcApproved'
                                        ))
                                }
                                index={index}
                                currencyType={expense.currency}
                            />
                        }
                        error={Boolean(errors.expenseEntries?.[index]?.amount)}
                        disabled={
                            disabled ||
                            isViewOnly ||
                            isInvoiceReviewLocked ||
                            (isPosted && isViewOnly) ||
                            isPendingSubmission ||
                            enableEditByCorrection ||
                            disableByCCAsCorrectionOrStatus ||
                            (isReimbursement && expense.source === 'Clinician') ||
                            (isReimbursement && index > 0) ||
                            (isReimbursement &&
                                index === 0 &&
                                expense.source === 'LtAssociate' &&
                                isAnyExpensePosted) ||
                            (isReimbursement &&
                                index === 0 &&
                                expense.source === 'LtAssociate' &&
                                originalExpense &&
                                !(
                                    originalExpense.status === 'NeedMoreInformation' ||
                                    originalExpense.status === 'InProcess' ||
                                    originalExpense.status === 'ReadyForReview' ||
                                    originalExpense.status === 'ReturnedByAccounting' ||
                                    originalExpense.status === 'TcApproved'
                                ))
                        }
                        helperText={errors.expenseEntries?.[index]?.amount?.message}
                    />
                )}
            />
            {creditCardAmountChangeWarning && (
                <Grid2 item size={{ md: 10 }}>
                    <Typography className={classes.errorMessage}>
                        {creditCardAmountChangeWarning}
                    </Typography>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].confirmAmountChangeWarning`}
                        defaultValue={false}
                        rules={{
                            required: true,
                        }}
                        render={({ onChange, value }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={value}
                                        onChange={(e) => onChange(e.target.checked)}
                                        color="primary"
                                        sx={{
                                            transform: 'scale(0.8)',
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        variant="body"
                                        style={{
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            color: !Boolean(
                                                !errors.expenseEntries?.[index]
                                                    ?.confirmAmountChangeWarning
                                            )
                                                ? COLORS.LT_PEPPER
                                                : COLORS.LT_MIDNIGHT,
                                        }}
                                    >
                                        Verify the updated charge amount is correct
                                        <span style={{ color: COLORS.LT_PEPPER }}>*</span>
                                    </Typography>
                                }
                            />
                        )}
                    />
                </Grid2>
            )}
        </Grid2>
    );
};

export default withStyles(styles)(ExpenseAmount);
